import { useMemo } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import { convertSpeed, getUnits } from '../pages/WorkoutAnalysisV2';

const SpeedAnalysisSection = ({ chunks }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const units = getUnits();
  const speedLabel = units === 'imperial' ? 'mph' : 'km/h';

  const formatTimeDiff = (seconds) => {
    const absSeconds = Math.abs(seconds);
    const minutes = Math.floor(absSeconds / 60);
    const secs = Math.round(absSeconds % 60);
    const sign = seconds < 0 ? '-' : '+';
    return `${sign}${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const speedCategories = useMemo(() => {
    const categories = {};
    const baseRanges = [
      { min: 0, max: 10, labelMetric: '0-10 km/h', labelImperial: '0-6 mph' },
      { min: 10, max: 15, labelMetric: '10-15 km/h', labelImperial: '6-9 mph' },
      { min: 15, max: 20, labelMetric: '15-20 km/h', labelImperial: '9-12 mph' },
      { min: 20, max: 25, labelMetric: '20-25 km/h', labelImperial: '12-16 mph' },
      { min: 25, max: 35, labelMetric: '25-35 km/h', labelImperial: '16-22 mph' },
      { min: 35, max: 45, labelMetric: '35-45 km/h', labelImperial: '22-28 mph' },
      { min: 45, max: 1000, labelMetric: '45+ km/h', labelImperial: '28+ mph' }
    ];

    baseRanges.forEach(range => {
      const label = units === 'imperial' ? range.labelImperial : range.labelMetric;
      categories[label] = [];
    });

    chunks.forEach(chunk => {
      const speed = chunk.avg_speed;
      const range = baseRanges.find(r => speed >= r.min && speed < r.max);
      if (range) {
        const label = units === 'imperial' ? range.labelImperial : range.labelMetric;
        categories[label].push(chunk);
      }
    });

    return categories;
  }, [chunks, units]);

  const calculateCategorySummary = (categoryChunks) => {
    if (categoryChunks.length === 0) return null;

    const totalTime = categoryChunks.reduce((sum, c) => sum + c.duration, 0);
    const totalDistance = categoryChunks.reduce((sum, c) => sum + c.distance, 0);
    const avgSpeed = categoryChunks.reduce((sum, c) => sum + c.avg_speed * c.duration, 0) / totalTime;
    const avgPower = categoryChunks.reduce((sum, c) => sum + c.avg_power * c.duration, 0) / totalTime;
    const avgGradient = categoryChunks.reduce((sum, c) => sum + c.avg_gradient * c.duration, 0) / totalTime;

    // Compute average curvature index for this category
    const curvatureSum = categoryChunks.reduce((sum, c) => sum + (typeof c.curvature_index === 'number' ? c.curvature_index : 0), 0);
    const curvatureCount = categoryChunks.filter(c => typeof c.curvature_index === 'number').length;
    const avgCurvatureIndex = curvatureCount > 0 ? Math.round(curvatureSum / curvatureCount) : null;
    const curvatureLabel = avgCurvatureIndex ? t(`twistiness${avgCurvatureIndex}`) : '';

    let timeDiffPosition = 0;
    let timeDiffWeight = 0;
    let timeDiffTotal = 0;
    let avgSpeedWithPosition = 0;
    let avgSpeedWithWeight = 0;
    let avgSpeedWithBoth = 0;
    
    const actualPositionCounts = {};
    const whatIfPositionCounts = {};

    categoryChunks.forEach(chunk => {
      if (chunk.what_if) {
        timeDiffPosition += chunk.what_if.time_diff_position;
        timeDiffWeight += chunk.what_if.time_diff_weight;
        timeDiffTotal += chunk.what_if.time_diff_total;
        avgSpeedWithPosition += chunk.what_if.speed_with_position_change * chunk.duration;
        avgSpeedWithWeight += chunk.what_if.speed_with_weight_change * chunk.duration;
        avgSpeedWithBoth += chunk.what_if.speed_with_both_changes * chunk.duration;
        
        const actualPos = chunk.what_if.actual_position;
        const whatIfPos = chunk.what_if.what_if_position;
        actualPositionCounts[actualPos] = (actualPositionCounts[actualPos] || 0) + 1;
        whatIfPositionCounts[whatIfPos] = (whatIfPositionCounts[whatIfPos] || 0) + 1;
      }
    });

    avgSpeedWithPosition = avgSpeedWithPosition / totalTime;
    avgSpeedWithWeight = avgSpeedWithWeight / totalTime;
    avgSpeedWithBoth = avgSpeedWithBoth / totalTime;
    
    const positionKeyMap = {
      'road_drops': 'roadDrops',
      'road_hoods': 'roadHoods',
      'road_aero_hoods': 'roadAeroHoods',
      'road_aero_aerobar': 'roadAeroAerobar',
      'road_aero_drops': 'roadAeroDrops',
      'gravel_hoods': 'gravelHoods',
      'gravel_drops': 'gravelDrops',
      'tt_aero': 'ttAero',
      'tt_base': 'ttBase'
    };
    
    const totalChunks = categoryChunks.filter(c => c.what_if).length;
    const actualPositionStr = Object.entries(actualPositionCounts)
      .map(([pos, count]) => `${Math.round(count / totalChunks * 100)}% ${t(positionKeyMap[pos] || pos)}`)
      .join(', ');
    const whatIfPositionStr = Object.entries(whatIfPositionCounts)
      .map(([pos, count]) => `${Math.round(count / totalChunks * 100)}% ${t(positionKeyMap[pos] || pos)}`)
      .join(', ');

    return {
      count: categoryChunks.length,
      totalTime,
      totalDistance,
      avgSpeed,
      avgPower,
      avgGradient,
      avgCurvatureIndex,
      curvatureLabel,
      timeDiffPosition,
      timeDiffWeight,
      timeDiffTotal,
      avgSpeedWithPosition,
      avgSpeedWithWeight,
      avgSpeedWithBoth,
      actualPosition: actualPositionStr,
      whatIfPosition: whatIfPositionStr
    };
  };

  const getTranslatedCategoryName = (category) => {
    switch(category) {
      case '0-10 km/h': return t('speed0to10');
      case '10-15 km/h': return t('speed10to15');
      case '15-20 km/h': return t('speed15to20');
      case '20-25 km/h': return t('speed20to25');
      case '25-35 km/h': return t('speed25to35');
      case '35-45 km/h': return t('speed35to45');
      case '45+ km/h': return t('speed45plus');
      case '0-6 mph': return t('speed0to6');
      case '6-9 mph': return t('speed6to9');
      case '9-12 mph': return t('speed9to12');
      case '12-16 mph': return t('speed12to16');
      case '16-22 mph': return t('speed16to22');
      case '22-28 mph': return t('speed22to28');
      case '28+ mph': return t('speed28plus');
      default: return category;
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
        {t('speedImpactAnalysis')}
      </Typography>
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={2}>
        {Object.entries(speedCategories).reverse().map(([category, categoryChunks]) => {
          const summary = calculateCategorySummary(categoryChunks);
          if (!summary) return null;

          return (
            <Paper
              key={category}
              elevation={3}
              sx={{
                p: 2,
                backgroundColor: colors.grey[700],
                borderRadius: "4px"
              }}
            >
              <Typography variant="h6" color={colors.grey[100]} fontWeight="bold" mb={1}>
                {getTranslatedCategoryName(category)}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                {t('timeLabel')}: {(summary.totalTime / 60).toFixed(1)} min
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                {t('avgPowerLabel')}: {summary.avgPower.toFixed(0)} W
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                {t('avgGradientLabel')}: {summary.avgGradient.toFixed(1)}%
              </Typography>
              {summary.avgCurvatureIndex !== null && (
                <Typography variant="body2" color={colors.grey[300]}>
                  {t('twistiness')}: {summary.curvatureLabel}
                  {summary.avgCurvatureIndex !== null && summary.curvatureLabel ? ` (${summary.avgCurvatureIndex})` : ''}
                </Typography>
              )}
              
              <Box mt={2} sx={{ borderTop: `1px solid ${colors.grey[600]}`, pt: 1 }}>
                <Typography variant="caption" color={colors.grey[400]} fontWeight="bold" display="block" mb={1}>
                  {t('whatIfAnalysisLabel')}:
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('scenario')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('speed')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('change')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('baseline')} ({summary.actualPosition})</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(summary.avgSpeed, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>-</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('positionOnly')} ({summary.whatIfPosition})</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(summary.avgSpeedWithPosition, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: Math.abs(summary.timeDiffPosition) < 30 ? colors.blueAccent[400] : summary.avgSpeedWithPosition >= summary.avgSpeed ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>
                          {summary.avgSpeedWithPosition >= summary.avgSpeed ? '+' : ''}{convertSpeed(summary.avgSpeedWithPosition - summary.avgSpeed, units).toFixed(2)} {speedLabel}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('weightOnly')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(summary.avgSpeedWithWeight, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: Math.abs(summary.timeDiffWeight) < 30 ? colors.blueAccent[400] : summary.avgSpeedWithWeight >= summary.avgSpeed ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>
                          {summary.avgSpeedWithWeight >= summary.avgSpeed ? '+' : ''}{convertSpeed(summary.avgSpeedWithWeight - summary.avgSpeed, units).toFixed(2)} {speedLabel}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('bothChanges')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{convertSpeed(summary.avgSpeedWithBoth, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: Math.abs(summary.timeDiffTotal) < 30 ? colors.blueAccent[400] : summary.avgSpeedWithBoth >= summary.avgSpeed ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '11px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>
                          {summary.avgSpeedWithBoth >= summary.avgSpeed ? '+' : ''}{convertSpeed(summary.avgSpeedWithBoth - summary.avgSpeed, units).toFixed(2)} {speedLabel}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography 
                  variant="body2" 
                  color={Math.abs(summary.timeDiffTotal) < 30 ? colors.blueAccent[400] : summary.timeDiffTotal < 0 ? colors.greenAccent[400] : colors.redAccent[400]}
                  fontWeight="bold"
                  mt={1}
                >
                  {t('totalTimeLabel')}: {formatTimeDiff(summary.timeDiffTotal)}
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default SpeedAnalysisSection;
